const COOKIE_MAX_LEN = 3800;

interface ICookieOptions {
  path?: string;
  expires?: number; // days until expiration
  domain?: string | null;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  shared?: boolean;
}

function getCookieValue(cookieName: string): string | undefined {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return value;
    }
  }
  return undefined;
}

function retrieveCookie(name) {
	const cookies = document.cookie.split(';');
	for (const cookie of cookies) {
		const eqIndex = cookie.indexOf('=');
		if (eqIndex === -1) continue;
		const key = cookie.slice(0, eqIndex).trim();
		if (key === name) return cookie.slice(eqIndex + 1).trim();
	}
	return undefined;
}

function createCookie(name: string, value: string, options?: ICookieOptions): void {
  const { domain, expires, path = '/', sameSite, secure } = options || {};
  let cookie = `${name}=${value}; path=${path}`;

  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 86400000);
    cookie += `; expires=${date.toUTCString()}`;
  }

  if (domain) {
    cookie += `; domain=${domain}`;
  }

  if (secure || sameSite === 'None') {
    cookie += '; secure';
  }

  if (sameSite) {
    cookie += `; samesite=${sameSite}`;
  }

  document.cookie = cookie;
}

function removeCookie(cookieName: string): void {
  const exp = new Date(0).toUTCString();
  const sharedDomain = getCookieDomain(true);
  const domain = getCookieDomain();

  const clearCookie = (name: string, domain?: string | null) => {
    const domainStr = domain ? `; domain=${domain}` : '';
    const path = window.location.pathname.replace(/\/$/, '');

    document.cookie = `${name}=; expires=${exp}; path=/${domainStr}`;
    document.cookie = `${name}=; expires=${exp}; path=${path}${domainStr}`;
  };

  if (sharedDomain) {
    clearCookie(cookieName, sharedDomain);
  }

  if (domain) {
    clearCookie(cookieName, domain);
  }

  clearCookie(cookieName);
}

function getCookieDomain(shared?: boolean): string | null {
  const domain = window?.location.hostname || '';

  if (!shared || domain === 'localhost' || !domain.includes('.')) {
    return null;
  }

  const chunks = domain.split('.');
  chunks.shift();
  return '.' + chunks.join('.');
}

function retrieve<T = string>(cookieName: string): T | string | null {
  const baseValue = getCookieValue(cookieName);
  if (baseValue) {
    return decodeURIComponent(baseValue);
  }

  const firstChunk = getCookieValue(`${cookieName}.1`);
  if (!firstChunk) {
    return null;
  }

  let value = firstChunk;
  for (let i = 2; ; i++) {
    const chunk = getCookieValue(`${cookieName}.${i}`);
    if (!chunk) break;
    value += chunk;
  }

  const decoded = decodeURIComponent(value);

  try {
    return JSON.parse(decoded) as T;
  } catch {
    return decoded;
  }
}

function store(cookieName: string, value: string | object, options?: ICookieOptions): void {
  const { expires = 0, path = '/', sameSite, secure, shared } = options || {};
  let valueToStore = value;

  if (typeof value === 'object') {
    valueToStore = JSON.stringify(value);
  }

  const encodedValue = encodeURIComponent(valueToStore as string);

  const cookieDomain = getCookieDomain(shared);

  if (encodedValue.length > COOKIE_MAX_LEN) {
    remove(cookieName);

    const cookiesCount = Math.ceil(encodedValue.length / COOKIE_MAX_LEN);

    Array.from({ length: cookiesCount }).forEach((_, index) => {
      const cookieValuePart = encodedValue.slice(index * COOKIE_MAX_LEN, (index + 1) * COOKIE_MAX_LEN);

      createCookie(cookieName + '.' + (index + 1), cookieValuePart, {
        domain: cookieDomain,
        expires,
        path,
        secure,
        sameSite,
      });
    });
  } else {
    createCookie(cookieName, encodedValue, {
      domain: cookieDomain,
      expires,
      path,
      secure,
      sameSite,
    });
  }
}

function remove(cookieName: string): void {
  const mainCookieValue = getCookieValue(cookieName);

  if (!mainCookieValue) {
    let i = 1;
    while (getCookieValue(cookieName + '.' + i++)) {
      removeCookie(cookieName + '.' + (i - 1));
    }
  } else {
    removeCookie(cookieName);
  }
}

export { retrieve, store, remove, retrieveCookie };
